import { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Badge } from 'react-bootstrap';
import { 
    FaDownload, FaMapMarkerAlt, FaUser, FaEnvelope, FaPhone, FaHome, 
    FaBuilding, FaExclamationTriangle 
} from 'react-icons/fa';

import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import laporanService from '../services/laporanService';

// --- FIX ICON LEAFLET (WAJIB TARUH PALING ATAS) ---
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: icon,
    iconRetinaUrl: icon,
    shadowUrl: iconShadow
});

// --- KONFIGURASI API URL ---
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/api";

const ProsesLaporanModal = ({ show, handleClose, laporan, onUpdate }) => {
    const [feedbackDeskripsi, setFeedbackDeskripsi] = useState('');
    const [status, setStatus] = useState('DIPROSES');
    const [feedbackDokumen, setFeedbackDokumen] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (laporan) {
            setFeedbackDeskripsi(laporan.feedbackDeskripsi || '');
            setStatus(laporan.status === 'BARU' ? 'DIPROSES' : laporan.status || 'DIPROSES');
            setFeedbackDokumen(null);
        }
    }, [laporan]);

    // Cek koordinat
    const lat = laporan?.koordinatLat ? parseFloat(laporan.koordinatLat) : null;
    const lng = laporan?.koordinatLng ? parseFloat(laporan.koordinatLng) : null;
    const hasCoordinates = lat && lng;
    const position = hasCoordinates ? [lat, lng] : [-6.2088, 106.8456]; // Default Jakarta

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!feedbackDeskripsi) {
            setError('Deskripsi feedback wajib diisi sebagai catatan tindak lanjut.');
            return;
        }

        const formData = new FormData();
        formData.append('feedbackDeskripsi', feedbackDeskripsi);
        formData.append('status', status);

        if (feedbackDokumen) {
            formData.append('feedbackDokumen', feedbackDokumen);
        }

        laporanService.prosesLaporan(laporan.id, formData)
            .then(() => {
                setSuccess('✅ Laporan berhasil diproses!');
                if (onUpdate) onUpdate();

                setTimeout(() => {
                    handleClose();
                    setSuccess('');
                }, 1500);
            })
            .catch(err => {
                console.error(err);
                setError('❌ Gagal memproses laporan. Cek koneksi atau ukuran file.');
            });
    };

    if (!laporan) return null;

    return (
        <Modal show={show} onHide={handleClose} centered size="xl">
            <Modal.Header closeButton className="bg-light">
                <Modal.Title>
                    <strong>Tindak Lanjut Laporan #{laporan.id}</strong>
                    <Badge 
                        bg={
                            laporan.status === 'BARU'
                                ? 'warning'
                                : laporan.status === 'DIPROSES'
                                ? 'primary'
                                : 'success'
                        }
                        className="ms-2 fs-6"
                    >
                        {laporan.status}
                    </Badge>
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}

                    <Row>
                        {/* ==================== KOLOM KIRI ==================== */}
                        <Col lg={5} className="border-end pe-lg-4">
                            <h6 className="text-primary fw-bold border-bottom pb-2 mb-3">
                                <FaUser className="me-2" /> Data Pelapor
                            </h6>

                            <div className="mb-3 small">
                                <div className="fw-bold fs-6">{laporan.namaPelapor}</div>
                                <div className="text-muted"><FaEnvelope className="me-1" /> {laporan.email || '-'}</div>
                                <div className="text-muted"><FaPhone className="me-1" /> {laporan.noTelp || '-'}</div>
                                <div className="text-muted mt-1"><FaHome className="me-1" /> {laporan.alamat || '-'}</div>
                            </div>

                            <h6 className="text-primary fw-bold border-bottom pb-2 mb-3 mt-4">
                                <FaExclamationTriangle className="me-2" /> Detail Kejadian
                            </h6>

                            <div className="mb-2">
                                <label className="text-muted small fw-bold">Kategori</label>
                                <div>{laporan.kategoriLaporan}</div>
                            </div>

                            <div className="mb-2">
                                <label className="text-muted small fw-bold">Ditujukan Kepada</label>
                                <div><FaBuilding className="me-1 text-secondary" /> {laporan.ditujukanKepada}</div>
                            </div>

                            <div className="mb-2">
                                <label className="text-muted small fw-bold">Pihak Terlapor</label>
                                <div className="text-danger fw-bold">{laporan.terlapor || '-'}</div>
                            </div>

                            <div className="mt-3">
                                <label className="text-muted small fw-bold">Dokumen Bukti Pelapor</label>
                                <div className="mt-1">
                                    {laporan.dokumenNama ? (
                                        <Button
                                            variant="outline-dark"
                                            size="sm"
                                            href={`${API_BASE_URL}/laporan/dokumen/${laporan.id}`}
                                            target="_blank"
                                            className="w-100 text-start"
                                        >
                                            <FaDownload className="me-2" /> Unduh: {laporan.dokumenNama}
                                        </Button>
                                    ) : (
                                        <span className="text-muted fst-italic">Tidak ada dokumen dilampirkan.</span>
                                    )}
                                </div>
                            </div>
                        </Col>

                        {/* ==================== KOLOM KANAN ==================== */}
                        <Col lg={7} className="ps-lg-4">
                            <h6 className="text-primary fw-bold border-bottom pb-2 mb-3">
                                <FaMapMarkerAlt className="me-2" /> Lokasi & Kronologi
                            </h6>

                            {/* MAP */}
                            <div
                                className="rounded overflow-hidden border mb-2"
                                style={{ height: '200px', width: '100%', background: '#eee' }}
                            >
                                {hasCoordinates ? (
                                    <MapContainer
                                        center={position}
                                        zoom={15}
                                        dragging={false}
                                        scrollWheelZoom={false}
                                        doubleClickZoom={false}
                                        style={{ height: '100%', width: '100%' }}
                                    >
                                        <TileLayer
                                            attribution="&copy; OSM"
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        />

                                        <Marker position={position}>
                                            <Popup>{laporan.lokasiKejadian}</Popup>
                                        </Marker>
                                    </MapContainer>
                                ) : (
                                    <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                        <small>Peta tidak tersedia (Koordinat tidak valid)</small>
                                    </div>
                                )}
                            </div>

                            <div className="mb-3 small bg-light p-2 rounded border">
                                <strong>Alamat TKP:</strong> {laporan.lokasiKejadian || "Tidak ada detail lokasi"}
                            </div>

                            <div className="mb-3">
                                <label className="text-muted small fw-bold">Deskripsi Laporan</label>
                                <div 
                                    className="p-3 bg-light rounded border"
                                    style={{ maxHeight: '150px', overflowY: 'auto' }}
                                >
                                    {laporan.deskripsiLaporan}
                                </div>
                            </div>

                            {/* FORM ADMIN */}
                            <div 
                                className="p-3 rounded border border-primary mt-4"
                                style={{ backgroundColor: '#f0f8ff' }}
                            >
                                <h6 className="fw-bold text-dark mb-3">Form Tindak Lanjut Petugas</h6>

                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold">Catatan / Feedback untuk Pelapor</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Tuliskan tindakan yang telah diambil..."
                                        value={feedbackDeskripsi}
                                        onChange={(e) => setFeedbackDeskripsi(e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Row>
                                    <Col md={7}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small fw-bold">Upload Bukti Penyelesaian (Opsional)</Form.Label>
                                            <Form.Control
                                                type="file"
                                                size="sm"
                                                onChange={(e) => setFeedbackDokumen(e.target.files[0])}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={5}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small fw-bold">Update Status</Form.Label>
                                            <Form.Select
                                                value={status}
                                                onChange={(e) => setStatus(e.target.value)}
                                                className={
                                                    status === 'SELESAI'
                                                        ? 'border-success text-success fw-bold'
                                                        : 'border-primary'
                                                }
                                            >
                                                <option value="DIPROSES">Sedang Diproses</option>
                                                <option value="SELESAI">✅ Selesai</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>

                <Modal.Footer className="bg-light">
                    <Button variant="secondary" onClick={handleClose}>
                        Batal
                    </Button>

                    <Button variant="primary" type="submit">
                        <FaExclamationTriangle className="me-2" /> Simpan Tindak Lanjut
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ProsesLaporanModal;
